import React, { useEffect } from "react";

import {
  Breakpoints,
  Checkbox,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextType,
  TextWeight
} from "@vw-marketing/us-components";

import {
  StyledChannelsContainer,
  StyledTopicContainer,
  StyledTopicData
} from "./styles";
import { ChannelState } from "../../types/types";
import { ClickableSupDisclaimer } from "../../../../shared/components/clickable-sup-disclaimer";
import { useDisclaimerReference } from "../../../../shared/hooks/use-disclaimer-reference";

interface TopicItemProps {
  channels: ChannelState[];
  topicDescription: string;
  topicName: string;
  /** Called with the channel's name when its checkbox is toggled. */
  onChannelToggle: (channelName: string) => void;
  /** Disclaimer copy for the topic name, resolved from config by purposeId. */
  nameDisclaimerText?: string;
  /** Disclaimer copy for the topic description, resolved from config by purposeId. */
  descriptionDisclaimerText?: string;
  /**
   * Reports the (text, assigned reference number) pair once the shared
   * disclaimer-manager has resolved a number for a registered disclaimer, so
   * an ancestor can build the footer/modal disclaimer list. Called once per
   * text whenever registration happens or the assigned number changes.
   */
  onDisclaimerRegistered?: (disclaimerText: string, disclaimerNumber: string) => void;
}

export const TopicItem = ({
  channels,
  topicDescription,
  topicName,
  onChannelToggle,
  nameDisclaimerText,
  descriptionDisclaimerText,
  onDisclaimerRegistered,
}: TopicItemProps) => {
  const nameDisclaimerNumber = useDisclaimerReference(nameDisclaimerText);
  const descriptionDisclaimerNumber = useDisclaimerReference(descriptionDisclaimerText);

  useEffect(() => {
    if (nameDisclaimerText && nameDisclaimerNumber) {
      onDisclaimerRegistered?.(nameDisclaimerText, nameDisclaimerNumber);
    }
  }, [nameDisclaimerText, nameDisclaimerNumber, onDisclaimerRegistered]);

  useEffect(() => {
    if (descriptionDisclaimerText && descriptionDisclaimerNumber) {
      onDisclaimerRegistered?.(descriptionDisclaimerText, descriptionDisclaimerNumber);
    }
  }, [descriptionDisclaimerText, descriptionDisclaimerNumber, onDisclaimerRegistered]);

  return (
    <StyledTopicContainer>
      <StyledTopicData className="topic-data">
        <TextCustom
          appearance={TextAppearance.copy200}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              color: TextColor.primary,
              fontType: TextType.Label,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight24,
            }
          }}
        >
          {topicName}
          {nameDisclaimerNumber && (
            <sup>
              <ClickableSupDisclaimer disclaimerNumber={nameDisclaimerNumber}>
                {nameDisclaimerNumber}
              </ClickableSupDisclaimer>
            </sup>
          )}
        </TextCustom>
        <TextCustom
          appearance={TextAppearance.copy100}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize14,
              color: TextColor.primary,
              fontType: TextType.Label,
              fontWeight: TextWeight.light,
              lineHeight: TextLineHeight.TextLineHeight24,
            }
          }}
        >
          {topicDescription}
          {descriptionDisclaimerNumber && (
            <sup>
              <ClickableSupDisclaimer disclaimerNumber={descriptionDisclaimerNumber}>
                {descriptionDisclaimerNumber}
              </ClickableSupDisclaimer>
            </sup>
          )}
        </TextCustom>
      </StyledTopicData>
      <StyledChannelsContainer className="channels-container">
        {channels.map((channel) => (
          <Checkbox
            key={channel.channelName}
            label={channel.channelName}
            value={channel.channelName}
            checked={channel.isOptedIn}
            disabled={!channel.isSupported}
            onChange={() => onChannelToggle(channel.channelName)}
            name={channel.channelName}
          />
        ))}
      </StyledChannelsContainer>
    </StyledTopicContainer>
  )
};
